// The React-owned module body (M1 Phase 4, PR 9 — the shell cutover).
//
// Phase 3 rendered every widget body via the `window.bentoReact` mount bridge
// (legacy `refreshModuleBody` handed the body element to React). Now that React
// renders the whole desktop module shell, the body is composed directly: this
// looks the title up in the same REGISTRY and renders the widget from the store's
// control values. No bridge, no `createRoot` per body — just JSX.
//
// The body re-renders automatically when this module's controls (or the fixture)
// change in the store, replacing the imperative `refreshModuleBody` call the
// legacy toggle listeners used to make.

import { useEffect } from "react";
import { useBentoStore, selectModuleFixture } from "../store/index.js";
import { REGISTRY, buildWidgetContext } from "../react-bridge.js";
import { ModuleTitleContext } from "./widgets/WidgetSkeleton.js";

export interface ModuleBodyProps {
  moduleId: string;
  title: string;
}

export default function ModuleBody({ moduleId, title }: ModuleBodyProps) {
  // Subscribe to just this module's controls + its fixture (the inputs a
  // widget context is built from). Selecting these (not the whole module) avoids
  // re-rendering the body on a drag/resize position change.
  const controls = useBentoStore(
    (s) => s.modules.find((m) => m.id === moduleId)?.controls
  );
  // Pinned modules stay on the match they were pinned on (legacy never refreshed
  // a pinned body on a fixture change); unpinning re-syncs. That match is the
  // module's `pinnedMatch` (saved with the board; Ben, Q-006), read through
  // `selectModuleFixture` so <ModuleControls> offers THAT match's teams too.
  const fixture = useBentoStore((s) => selectModuleFixture(s, moduleId));

  // Market modules rely on a cross-module row highlight that stays in main.js
  // (the Phase-3/4 boundary). It reads the paired module's controlled <select>,
  // which lags the store until React flushes, so the glue defers internally
  // (setTimeout 0). Re-run it after every body render — the same point the
  // legacy refreshModuleBody re-applied it. A no-op for non-market modules.
  useEffect(() => {
    window.bentoShell?.afterBodyRender?.(moduleId, title);
  });

  const Component = REGISTRY[title];
  if (!Component) return null; // every current title is registered; guard anyway
  return (
    <ModuleTitleContext value={title}>
      <Component {...buildWidgetContext(fixture, controls)} />
    </ModuleTitleContext>
  );
}
